import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { loadStripe } from '@stripe/stripe-js';
import { Elements, PaymentElement, useStripe, useElements } from '@stripe/react-stripe-js';
import axiosInstance from "../api/axiosConfig";

// Checkout Form Component
const CheckoutForm = ({ 
  transactionId, 
  amount, 
  onSuccess, 
  onError, 
  onCancel,
  donorEmail,
  donorName,
  clientSecret // Add this
}) => {
  const stripe = useStripe();
  const elements = useElements();
  const [processing, setProcessing] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!stripe || !elements) {
      setErrorMessage('Stripe not initialized');
      return;
    }
    
    setProcessing(true);
    setErrorMessage('');

    try {
      const { error, paymentIntent } = await stripe.confirmPayment({
        elements,
        confirmParams: {
          return_url: `${window.location.origin}/donation-return`,
          receipt_email: donorEmail,
        },
        redirect: 'if_required',
      });

      if (error) {
        console.error('Payment confirmation error:', error);
        setErrorMessage(error.message);
        onError(error.message);
      } else if (paymentIntent && paymentIntent.status === 'succeeded') {
        console.log('Payment succeeded:', paymentIntent);
        
        // Confirm payment with backend
        try {
          const response = await axiosInstance.post('/donations/stripe/confirm', {
            transaction_id: transactionId,
            payment_intent_id: paymentIntent.id
          });

          if (response.data.success) {
            onSuccess({
              transactionId,
              amount,
              paymentIntentId: paymentIntent.id,
              status: 'completed',
              receipt: response.data.data.receipt
            });
          } else {
            throw new Error(response.data.message || 'Failed to confirm payment');
          }
        } catch (confirmError) {
          console.error('Backend confirmation error:', confirmError);
          setErrorMessage(confirmError.message);
          onError(confirmError.message);
        }
      }
    } catch (err) {
      console.error('Payment processing error:', err);
      setErrorMessage(err.message);
      onError(err.message);
    } finally {
      setProcessing(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="p-4">
      <div className="mb-4">
        <div className="d-flex justify-content-between align-items-center mb-3">
          <h5 className="fw-bold mb-0">Payment Details</h5>
        </div>
        
        {/* Stripe Payment Element */}
        <div className="mb-3">
          <PaymentElement />
        </div>
      </div>
      
      {errorMessage && (
        <div className="alert alert-danger mt-2">
          <i className="bi bi-exclamation-triangle-fill me-2"></i>
          {errorMessage}
        </div>
      )}
      
      <div className="d-flex gap-2 mt-4">
        <button
          type="button"
          className="btn btn-outline-secondary flex-grow-1 py-2 rounded-pill"
          onClick={onCancel}
          disabled={processing}
        >
          Cancel
        </button>
        <button
          type="submit"
          className="btn btn-warning text-white flex-grow-1 py-2 rounded-pill"
          disabled={!stripe || processing}
        >
          {processing ? (
            <>
              <span className="spinner-border spinner-border-sm me-2"></span>
              Processing...
            </>
          ) : (
            `Pay $${amount?.toFixed(2)}`
          )}
        </button>
      </div>

      {/* Security Badge */}
      <div className="text-center mt-3">
        <small className="text-muted">
          <i className="bi bi-shield-lock me-1"></i>
          Secured by Stripe • 256-bit SSL encryption
        </small>
      </div>
    </form>
  );
};

// Main Modal Component - FIXED: Removed extra API call
const StripeElementsModal = ({ 
  isOpen, 
  onClose, 
  onCancel, 
  onSuccess, 
  onError,
  amount, 
  transactionId,
  donorEmail,
  donorName,
  clientSecret, // Receive from parent
  publishableKey // Receive from parent
}) => {
  const [stripePromise, setStripePromise] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [options, setOptions] = useState(null);

  useEffect(() => {
    if (isOpen && clientSecret && publishableKey) {
      // Initialize Stripe with the provided client secret
      initializeStripe();
    } else {
      setLoading(true);
    }
  }, [isOpen, clientSecret, publishableKey]);

  const initializeStripe = async () => {
    setLoading(true);
    setError(null);
    
    try {
      // Load Stripe with publishable key
      const stripe = await loadStripe(publishableKey);
      setStripePromise(stripe);
      
      // Set options with client secret
      setOptions({
        clientSecret: clientSecret,
        appearance: {
          theme: 'stripe',
          variables: {
            colorPrimary: '#ffc107',
            colorBackground: '#ffffff',
            colorText: '#1a1a1a',
            colorDanger: '#df1b41',
            fontFamily: 'system-ui, -apple-system, sans-serif',
            borderRadius: '8px',
          },
          rules: {
            '.Input': {
              border: '1px solid #e2e8f0',
              fontSize: '16px',
            },
            '.Input:focus': {
              borderColor: '#ffc107',
              boxShadow: '0 0 0 3px rgba(255, 193, 7, 0.1)',
            },
            '.Label': {
              fontSize: '14px',
              fontWeight: '500',
              marginBottom: '4px',
            },
          },
        },
      });
      
      setLoading(false);
    } catch (err) {
      console.error('Stripe initialization error:', err);
      setError(err.message);
      onError(err.message);
      setLoading(false);
    }
  };

  const handleCancel = () => {
    onCancel({ 
      transactionId, 
      reason: 'user_cancelled',
      timestamp: new Date().toISOString() 
    });
    onClose();
  };

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          className="modal fade show d-block"
          style={{ 
            backgroundColor: 'rgba(0,0,0,0.5)', 
            zIndex: 1050,
            overflowY: 'auto'
          }}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          onClick={(e) => e.target === e.currentTarget && handleCancel()}
        >
          <div className="modal-dialog modal-dialog-centered modal-lg">
            <motion.div
              className="modal-content"
              initial={{ y: 50, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              exit={{ y: 50, opacity: 0 }}
              transition={{ duration: 0.3 }}
            >
              {/* Modal Header */}
              <div className="modal-header text-white" style={{ backgroundColor: "#4b0000" }}>
                <h5 className="modal-title">
                  <i className="bi bi-credit-card me-2"></i>
                  Complete Your Payment
                </h5>
                <button
                  type="button"
                  className="btn-close btn-close-white"
                  onClick={handleCancel}
                  disabled={loading}
                ></button>
              </div>

              {/* Modal Body */}
              <div className="modal-body p-0">
                {loading ? (
                  <div className="text-center py-5">
                    <div className="spinner-border text-warning mb-3" style={{ width: '3rem', height: '3rem' }}></div>
                    <p className="text-muted">Loading secure payment form...</p>
                  </div>
                ) : error ? (
                  <div className="text-center py-5 px-4">
                    <i className="bi bi-exclamation-triangle-fill text-danger fs-1 mb-3"></i>
                    <h5 className="text-danger mb-2">Payment Initialization Failed</h5>
                    <p className="text-muted mb-3">{error}</p>
                    <button
                      className="btn btn-outline-secondary rounded-pill px-4"
                      onClick={handleCancel}
                    >
                      Close
                    </button>
                  </div>
                ) : (
                  stripePromise && clientSecret && options && (
                    <Elements stripe={stripePromise} options={options}>
                      <CheckoutForm
                        transactionId={transactionId}
                        amount={amount}
                        donorEmail={donorEmail}
                        donorName={donorName}
                        clientSecret={clientSecret}
                        onSuccess={onSuccess}
                        onError={onError}
                        onCancel={handleCancel}
                      />
                    </Elements>
                  )
                )}
              </div>

              {/* Modal Footer */}
              <div className="modal-footer bg-light d-flex justify-content-between">
                <small className="text-muted">
                  <i className="bi bi-patch-check-fill text-warning me-1"></i>
                  PCI-DSS Compliant
                </small>
                <small className="fw-bold">${amount?.toFixed(2)} AUD</small>
              </div>
            </motion.div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default StripeElementsModal;